// Single source of truth for topical clusters — powers hub-and-spoke internal
// linking across blog articles, product pages and the ingredients hub.
//
// Editing this file changes every RelatedReading / ClusterLinks block on the
// site. Add a new article: put its slug + title + excerpt in ARTICLES, then
// add the slug to the correct cluster's `articleSlugs`.

export type ClusterKey = "magnesium" | "liver-gut" | "hormonal" | "festive";

export type Cluster = {
  key: ClusterKey;
  label: string;
  productHandle: "mag-absorb-pro" | "liver-biome-plus" | "ova-balance";
  productTitle: string;
  productTagline: string;
  productBenefits: string[];
  ingredientAnchor: string; // hash target on /ingredients (matches section id)
  ingredientLabel: string;
  articleSlugs: string[];
};


export type Article = {
  slug: string;
  title: string;
  excerpt: string;
  cluster: ClusterKey;
};

export const ARTICLES: Article[] = [
  {
    slug: "diwali-healthy-gifts-ideas",
    title: "Diwali Healthy Gifts Ideas for Family, Friends & Colleagues",
    excerpt: "Thoughtful Diwali gifts people keep using after the festival: sleep, liver and cycle support, with notes on who each suits.",
    cluster: "festive",
  },
  {
    slug: "magnesium-for-migraines",
    title: "Magnesium for Migraines: Benefits, Best Forms & Dosage",
    excerpt: "A practical look at migraine trials, magnesium forms, studied doses and safety considerations.",
    cluster: "magnesium",
  },
  {
    slug: "magnesium-benefits",
    title: "Magnesium Benefits: Forms, Foods & Dosage Guide",
    excerpt:
      "The four magnesium forms in Mag Absorb Pro and how they compare on bioavailability, sleep and recovery.",
    cluster: "magnesium",
  },
  {
    slug: "best-magnesium-supplement-for-sleep",
    title: "Best Magnesium Supplement for Sleep: A Complete Guide",
    excerpt:
      "How magnesium supports sleep, the best forms to look for, timing, dosage and how to choose a quality supplement.",
    cluster: "magnesium",
  },
  {
    slug: "magnesium-glycinate-vs-citrate-india",
    title: "Magnesium Bisglycinate vs Citrate vs Chloride: India Buyer's Guide",
    excerpt:
      "Which magnesium form is best in India bisglycinate, citrate, chloride, oxide or aspartate and how to read a real label.",
    cluster: "magnesium",
  },
  {
    slug: "magnesium-deficiency-symptoms-india",
    title: "9 Signs of Magnesium Deficiency Indians Commonly Miss",
    excerpt:
      "Fatigue, cramps, poor sleep, palpitations, migraines, nine everyday signs of low magnesium in Indian adults.",
    cluster: "magnesium",
  },
  {
    slug: "magnesium-rich-foods-india",
    title: "15 Magnesium-Rich Foods in the Indian Diet",
    excerpt:
      "Foods high in magnesium in the Indian kitchen, per-serving amounts, absorption blockers and a realistic 350 mg day.",
    cluster: "magnesium",
  },
  {
    slug: "natural-remedies-sleep-anxiety-india",
    title: "Natural Ways to Deal With Sleep Anxiety (India Guide)",
    excerpt:
      "Racing thoughts at 2 AM? A practical, evidence-based India guide to calming the nervous system before bed.",
    cluster: "magnesium",
  },
  {
    slug: "cant-sleep-at-night-what-to-do-magnesium",
    title: "Can't Sleep at Night? What to Do Tonight and Whether Magnesium Helps",
    excerpt:
      "What to do tonight when sleep will not come, why you keep waking up, and an honest look at magnesium form, dose, timing and safety.",
    cluster: "magnesium",
  },
  {
    slug: "shareer-ki-thakaan-dukaandaar-magnesium",
    title: "Shareer Ki Thakaan: Dukandaar Ke Liye Guide",
    excerpt:
      "Dukaan ke baad ki thakaan aur pair mein dard: karan, routine changes aur magnesium ka role.",
    cluster: "magnesium",
  },
  {
    slug: "leg-cramps-at-night-causes-india",
    title: "Leg Cramps at Night: Causes, Fixes & Red Flags",
    excerpt:
      "The 6 real causes of nocturnal calf cramps in Indian adults and the 3-week protocol that stops them.",
    cluster: "magnesium",
  },
  {
    slug: "liver-detox-truth",
    title: "Liver Detox: Myths vs Evidence. What Actually Works",
    excerpt:
      "The juice-cleanse industry is built on a myth. Here's what the liver actually does and what's research-backed.",
    cluster: "liver-gut",
  },
  {
    slug: "gut-liver-axis",
    title: "The Gut-Liver Axis: How Your Gut Controls Your Liver Health",
    excerpt:
      "Your liver processes 70% of its blood from the gut. How dysbiosis drives fatty liver and inflammation.",
    cluster: "liver-gut",
  },
  {
    slug: "curcumin-bioavailability",
    title: "Curcumin Bioavailability: Liposomal vs Piperine vs Standard",
    excerpt:
      "Standard curcumin has very low oral bioavailability. How liposomal delivery and Bio-Absorb Matrix support absorption.",
    cluster: "liver-gut",
  },
  {
    slug: "sgot-and-sgpt-normal-range",
    title: "SGOT and SGPT Normal Range: What High Liver Enzymes Mean",
    excerpt:
      "Normal SGOT and SGPT ranges, what high liver enzymes mean, danger levels and when to see a doctor.",
    cluster: "liver-gut",
  },
  {
    slug: "sluggish-liver-signs-and-fix",
    title: "Signs of a Sluggish Liver and How to Fix It",
    excerpt:
      "Ten early signs a hardworking Indian liver is falling behind, and a 90-day protocol that reverses it.",
    cluster: "liver-gut",
  },
  {
    slug: "fatty-liver-diet-plan-india",
    title: "Fatty Liver (NAFLD) Diet Plan. Indian Foods That Help and Hurt",
    excerpt:
      "A practical Indian NAFLD diet plan, what to eat, what to cut, and a realistic Indian day.",
    cluster: "liver-gut",
  },
  {
    slug: "bloating-after-meals-causes-india",
    title: "Bloating After Every Meal? Root Causes + a 4-Week Reset",
    excerpt:
      "The 6 root causes of post-meal bloating in Indian adults and a 4-week reset that actually works.",
    cluster: "liver-gut",
  },
  {
    slug: "best-liver-detox-supplement-india-guide",
    title: "Best Liver Detox Supplement in India: A Complete Buyer's Guide",
    excerpt:
      "What to look for on a liver supplement label in India \u2014 NAC, liposomal silymarin, a real probiotic, and the red flags to avoid.",
    cluster: "liver-gut",
  },
  {
    slug: "hangover-liver-recovery-fatty-liver",
    title: "From Hangover to Fatty Liver: What Your Liver Actually Needs After Alcohol and Processed Food",
    excerpt:
      "Honest guide to hangovers, liver recovery after drinking, fatty liver reversal, and what NAC, silymarin and probiotics can and cannot do.",
    cluster: "liver-gut",
  },
  {
    slug: "fatty-liver-insulin-resistance-diabetes",
    title: "Fatty Liver and Diabetes: How Liver Fat and Insulin Resistance Are Connected",
    excerpt:
      "How fatty liver, insulin resistance, prediabetes and type 2 diabetes are connected — and what steps may support metabolic health.",
    cluster: "liver-gut",
  },
  {
    slug: "inositol-and-hair",
    title: "Inositol and Hair: Can It Help With PCOS Hair Loss and Excess Hair Growth?",
    excerpt:
      "Stronger evidence for hirsutism than scalp regrowth, plus a realistic 3-6 month timeline.",
    cluster: "hormonal",
  },
  {
    slug: "inositol-pcos",
    title: "Inositol for PCOS: The 40:1 Myo:D-Chiro Ratio Explained",
    excerpt:
      "Why the gold-standard 40:1 ratio outperforms metformin in many PCOS trials.",
    cluster: "hormonal",
  },
  {
    slug: "berberine-guide",
    title: "Berberine: Benefits, Dosage & Evidence",
    excerpt:
      "Berberine activates AMPK like metformin and exercise. Blood sugar, lipids, fatty liver, PCOS.",
    cluster: "hormonal",
  },
  {
    slug: "pcos-inositol-guide-india",
    title: "How to Manage PCOS Naturally With Inositol. India Guide",
    excerpt:
      "Dose, timing, myo vs D-chiro, what to expect at 3 and 6 months, and how inositol compares with metformin.",
    cluster: "hormonal",
  },
  {
    slug: "liver-pcos-connection-india",
    title: "The Liver–PCOS Connection: Fatty Liver, Estrogen Clearance & Why Inositol Alone Stalls",
    excerpt:
      "Insulin resistance drives both PCOS and fatty liver. How sluggish clearance feeds hormonal acne and PCOS fatigue, and where inositol, berberine and magnesium fit.",
    cluster: "hormonal",
  },
  {
    slug: "irregular-periods-pcos-checklist",
    title: "Irregular Periods & PCOS: A 7-Point Checklist Before You Panic",
    excerpt:
      "Bloodwork, ultrasound, thyroid, insulin, prolactin, a 7-point workup that separates PCOS from other causes.",
    cluster: "hormonal",
  },
  {
    slug: "berberine-vs-metformin-india",
    title: "Berberine vs Metformin: What the Trials Actually Show",
    excerpt:
      "A head-to-head of the human trials on insulin, lipids, PCOS and fatty liver, when berberine is enough, when metformin wins.",
    cluster: "hormonal",
  },
  {
    slug: "inositol-vs-berberine-insulin-resistance-pmos",
    title: "Inositol vs Berberine for Insulin Resistance in PCOS / PMOS",
    excerpt: "How each works, what the trials show, side effects and whether to combine them.",
    cluster: "hormonal",
  },
  {
    slug: "pcod-vs-pcos-vs-pmos",
    title: "PCOS is now PMOS and it is more than an ovarian condition",
    excerpt: "Understand the name change, the wider health picture and questions to bring to your next appointment.",
    cluster: "hormonal",
  },
  {
    slug: "pcos-explained-symptoms-diagnosis-india",
    title: "PCOS Explained: Symptoms, Diagnosis and What to Do Next",
    excerpt:
      "A calm India-focused guide to PCOS symptoms, diagnosis, PCOD vs PCOS, fertility, diet, and when to speak with a doctor.",
    cluster: "hormonal",
  },
  {
    slug: "why-is-my-period-late",
    title: "Why Is My Period Late? Causes, Tests & When to See a Doctor",
    excerpt:
      "Pregnancy, stress, PCOS, thyroid, weight changes, over-exercise, contraception and perimenopause, and when a delayed period needs investigating.",
    cluster: "hormonal",
  },
  {
    slug: "pcos-metabolic-health-prediabetes",
    title: "PCOS Is More Than an Irregular Period",
    excerpt:
      "Even with normal glucose, PCOS was linked to a 23% higher relative hazard of progressing to prediabetes. What that means and how screening is recommended.",
    cluster: "hormonal",
  },
  {
    slug: "where-to-buy-inositol-for-pcos-supplements-online-india",
    title: "Where to Buy Inositol Supplements Online in India",
    excerpt:
      "How to read an inositol label before you order: the ratio, the amount per sachet, the other actives and the cost per day.",
    cluster: "hormonal",
  },
  {
    slug: "liver-health-supplements-india",
    title: "Liver Health Supplements: What Actually Helps",
    excerpt:
      "The actives with real evidence behind them, how to read an Indian label in ninety seconds, and where everyday nutrition does more than any capsule.",
    cluster: "liver-gut",
  },
  {
    slug: "best-probiotic-for-gut-health-india",
    title: "Best Probiotic for Gut Health and Digestion",
    excerpt:
      "Why strain names beat CFU counts, how long a probiotic really takes to work, and where curd and fermented food fit alongside a capsule.",
    cluster: "liver-gut",
  },
  {
    slug: "7-day-pcos-diet-plan-india",
    title: "7-Day PCOS Diet Plan (India): What to Eat Each Day",
    excerpt:
      "A full week of real Indian meals built on insulin-friendly eating — the plate method, the day-by-day chart, and the foods worth limiting.",
    cluster: "hormonal",
  },
  {
    slug: "how-to-take-inositol-for-pcos-india",
    title: "Inositol for PCOS: How Much, When and How Long",
    excerpt:
      "The 40:1 myo to D-chiro ratio explained simply, the daily amount used in research, and a realistic three-month timeline.",
    cluster: "hormonal",
  },
];

export const CLUSTERS: Record<ClusterKey, Cluster> = {
  magnesium: {
    key: "magnesium",
    label: "Sleep, calm & recovery",
    productHandle: "mag-absorb-pro",
    productTitle: "Mag Absorb Pro",
    productTagline: "Four magnesium forms for sleep, muscle recovery & calm",
    productBenefits: [
      "Aquamin® + bisglycinate + chloride + aspartate",
      "Deeper sleep, fewer cramps, calmer mornings",
      "Third-party tested at research-backed doses",
    ],
    ingredientAnchor: "mag-absorb-pro",
    ingredientLabel: "Magnesium & cofactors",
    articleSlugs: [
      "magnesium-for-migraines",
      "magnesium-benefits",
      "best-magnesium-supplement-for-sleep",
      "magnesium-glycinate-vs-citrate-india",
      "magnesium-deficiency-symptoms-india",
      "magnesium-rich-foods-india",
      "natural-remedies-sleep-anxiety-india",
      "leg-cramps-at-night-causes-india",
      "cant-sleep-at-night-what-to-do-magnesium",
      "shareer-ki-thakaan-dukaandaar-magnesium",
    ],
  },
  "liver-gut": {
    key: "liver-gut",
    label: "Liver & gut axis",
    productHandle: "liver-biome-plus",
    productTitle: "Liver Biome Plus",
    productTagline: "Liposomal liver actives + a targeted gut biome stack",
    productBenefits: [
      "Liposomal Curcumin, Silymarin, ALA + NAC",
      "Supports Phase I/II detox and gut barrier",
      "Liposomal carriers + Bio-Absorb Matrix delivery support",
    ],
    ingredientAnchor: "liver-biome", // matches ingredients page section id
    ingredientLabel: "Liver & gut actives",
    articleSlugs: [
      "liver-detox-truth",
      "gut-liver-axis",
      "curcumin-bioavailability",
      "sluggish-liver-signs-and-fix",
      "sgot-and-sgpt-normal-range",
      "fatty-liver-diet-plan-india",
      "bloating-after-meals-causes-india",
      "best-liver-detox-supplement-india-guide",
      "hangover-liver-recovery-fatty-liver",
      "fatty-liver-insulin-resistance-diabetes",
      "liver-health-supplements-india",
      "best-probiotic-for-gut-health-india",
    ],
  },
  hormonal: {
    key: "hormonal",
    label: "Hormonal balance & PCOS",
    productHandle: "ova-balance",
    productTitle: "Ova Balance",
    productTagline: "40:1 Myo:D-Chiro inositol with Berberine HCl & Quatrefolic®",
    productBenefits: [
      "Gold-standard 40:1 inositol ratio",
      "Berberine HCl for insulin & AMPK support",
      "Quatrefolic® L-5-MTHF, the active folate",
    ],
    ingredientAnchor: "ova-balance",
    ingredientLabel: "Inositol & metabolic actives",
    articleSlugs: [
      "inositol-and-hair",
      "inositol-pcos",
      "berberine-guide",
      "pcos-inositol-guide-india",
      "liver-pcos-connection-india",
      "irregular-periods-pcos-checklist",
      "berberine-vs-metformin-india",
      "inositol-vs-berberine-insulin-resistance-pmos",
      "pcod-vs-pcos-vs-pmos",
      "pcos-explained-symptoms-diagnosis-india",
      "why-is-my-period-late",
      "pcos-metabolic-health-prediabetes",
      "where-to-buy-inositol-for-pcos-supplements-online-india",
      "how-to-take-inositol-for-pcos-india",
      "7-day-pcos-diet-plan-india",
    ],
  },
  festive: {
    key: "festive",
    label: "Festive gifting ideas",
    productHandle: "mag-absorb-pro",
    productTitle: "Mag Absorb Pro",
    productTagline: "A calm-evening gift for someone who can't switch off",
    productBenefits: [
      "Five magnesium forms in one evening routine",
      "Easy to gift, easy to keep using",
      "Third-party tested",
    ],
    ingredientAnchor: "mag-absorb-pro",
    ingredientLabel: "Magnesium & cofactors",
    articleSlugs: ["diwali-healthy-gifts-ideas"],
  },
};


/**
 * Related articles for a given article slug.
 * - In-cluster siblings first (excluding the current article).
 * - Then one cross-cluster suggestion, capped to `limit`.
 */
export function relatedArticles(currentSlug: string, limit = 3): Article[] {
  const current = ARTICLES.find((a) => a.slug === currentSlug);
  if (!current) return [];
  const sameCluster = ARTICLES.filter(
    (a) => a.cluster === current.cluster && a.slug !== currentSlug,
  );
  const otherCluster = ARTICLES.filter((a) => a.cluster !== current.cluster);
  return [...sameCluster, ...otherCluster].slice(0, limit);
}

/**
 * Related articles for a product page (cluster spokes for that SKU).
 */
export function articlesForProduct(handle: string, limit = 3): Article[] {
  const cluster = Object.values(CLUSTERS).find((c) => c.productHandle === handle);
  if (!cluster) return [];
  return cluster.articleSlugs
    .map((s) => ARTICLES.find((a) => a.slug === s))
    .filter((a): a is Article => Boolean(a))
    .slice(0, limit);
}

/**
 * Guide article for a given ingredient section (used on /ingredients).
 */
export function guideForIngredientAnchor(anchor: string): Article | undefined {
  const cluster = Object.values(CLUSTERS).find((c) => c.ingredientAnchor === anchor);
  if (!cluster) return undefined;
  const slug = cluster.articleSlugs[0];
  return ARTICLES.find((a) => a.slug === slug);
}
